import { Section, TextField } from '@dispatch/web/ui';
import { Grid, Typography } from '@mui/material';
import { Control, FieldErrors } from 'react-hook-form';

const styles = {
  item: {
    '.MuiGrid-item': {
      paddingLeft: '24px',
    },
  },
};

export const ClaimCapacityBulk = ({
  control,
  errors,
  isFieldCapacityByDayEnabled = false,
}: {
  control: Control;
  errors: FieldErrors;
  /**
   * Per-tenant kill-switch for the per-day field capacity model (ADO #94651).
   *
   * The static Field input renders either way -- bulk update must be able to set
   * the static field capacity with the per-day feature enabled (SU 08/04). The
   * flag only adds a caption, because with per-day live the effect of this value
   * depends on each selected resource's mode:
   *
   * - STATIC (or unset) resource -> the value is live immediately.
   * - PER_DAY resource -> the value is stored but dormant; the resource keeps its
   *   mode and its per-day grid, and the new static value applies only if per-day
   *   is later switched off for the tenant or the resource is moved to Static.
   *
   * This screen writes only `generalInformation.claimCapacity`: `BulkUpdatePostBody`
   * carries no `fieldCapacityMode` or `fieldCapacityByDay` member, so a bulk update
   * cannot change a resource's mode or its per-day values. Bulk per-day authoring
   * remains out of scope.
   */
  isFieldCapacityByDayEnabled?: boolean;
}) => {
  return (
    <div data-testid="claimOwnerCapacity">
      <Section title="Claim Capacity">
        <Grid container spacing={1} gap={2} padding="10px 25px">
          <Grid container xs={5} spacing={1}>
            <Grid container item>
              <Grid item xs={12}>
                <Typography component="p" fontSize="14px">
                  Handler
                </Typography>
              </Grid>
            </Grid>
            <Grid container item spacing={2} sx={styles.item}>
              <Grid item xs={8}>
                <TextField
                  id="claimCapacity"
                  name="generalInformation.claimCapacity"
                  label="Field"
                  type="number"
                  control={control}
                  inputProps={{
                    readOnly: false,
                  }}
                  error={
                    errors?.['generalInformation']?.['claimCapacity']?.message
                  }
                />
                {isFieldCapacityByDayEnabled && (
                  <Typography
                    component="p"
                    fontSize="12px"
                    data-testid="bulk-field-capacity-static-note"
                  >
                    Updates the static Field capacity. Resources set to Per day
                    keep their per-day values and their mode.
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Grid container item spacing={2} sx={styles.item}>
              <Grid item xs={8}>
                <TextField
                  id="virtualClaimCapacity"
                  name="generalInformation.virtualClaimCapacity"
                  label="Virtual"
                  type="number"
                  control={control}
                  inputProps={{
                    readOnly: false,
                  }}
                  error={
                    errors?.['generalInformation']?.['virtualClaimCapacity']
                      ?.message
                  }
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2} sx={styles.item}>
              <Grid item xs={8}>
                <TextField
                  id="complexLossClaimCapacity"
                  name="generalInformation.complexLossClaimCapacity"
                  label="Complex Loss - Weekly"
                  type="number"
                  control={control}
                  inputProps={{
                    readOnly: false,
                  }}
                  error={
                    errors?.['generalInformation']?.['complexLossClaimCapacity']
                      ?.message
                  }
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2} sx={styles.item}>
              <Grid item xs={8} height="64px"></Grid>
            </Grid>
          </Grid>

          <Grid container xs={7} spacing={1}>
            <Grid container item>
              <Grid item xs={12}>
                <Typography component="p" fontSize="14px">
                  Owner
                </Typography>
              </Grid>
            </Grid>
            <Grid container item spacing={2} sx={styles.item}>
              <Grid item xs={6}>
                <TextField
                  id="claimOwner.onSunday"
                  name="claimOwner.onSunday"
                  label="Sunday"
                  type="number"
                  control={control}
                  inputProps={{
                    readOnly: false,
                  }}
                  error={errors?.['claimOwner']?.['onSunday']?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="claimOwner.onMonday"
                  name="claimOwner.onMonday"
                  label="Monday"
                  type="number"
                  control={control}
                  inputProps={{
                    readOnly: false,
                  }}
                  error={errors?.['claimOwner']?.['onMonday']?.message}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2} sx={styles.item}>
              <Grid item xs={6}>
                <TextField
                  id="claimOwner.onTuesday"
                  name="claimOwner.onTuesday"
                  label="Tuesday"
                  type="number"
                  control={control}
                  inputProps={{ readOnly: false }}
                  error={errors?.['claimOwner']?.['onTuesday']?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="claimOwner.onWednesday"
                  name="claimOwner.onWednesday"
                  label="Wednesday"
                  type="number"
                  control={control}
                  inputProps={{ readOnly: false }}
                  error={errors?.['claimOwner']?.['onWednesday']?.message}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2} sx={styles.item}>
              <Grid item xs={6}>
                <TextField
                  id="claimOwner.onThursday"
                  name="claimOwner.onThursday"
                  label="Thursday"
                  type="number"
                  control={control}
                  inputProps={{ readOnly: false }}
                  error={errors?.['claimOwner']?.['onThursday']?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="claimOwner.onFriday"
                  name="claimOwner.onFriday"
                  label="Friday"
                  type="number"
                  control={control}
                  inputProps={{ readOnly: false }}
                  error={errors?.['claimOwner']?.['onFriday']?.message}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2} sx={styles.item}>
              <Grid item xs={6}>
                <TextField
                  id="claimOwner.onSaturday"
                  name="claimOwner.onSaturday"
                  label="Saturday"
                  type="number"
                  control={control}
                  inputProps={{ readOnly: false }}
                  error={errors?.['claimOwner']?.['onSaturday']?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="claimOwner.maxWeeklyCapacity"
                  name="claimOwner.maxWeeklyCapacity"
                  label="Max Weekly Capacity"
                  type="number"
                  control={control}
                  error={errors?.['claimOwner']?.['maxWeeklyCapacity']?.message}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Section>
    </div>
  );
};
